import { ROUTES } from '@/constants/routes';
import { useSavReminders } from '@/hooks/useSavReminders';
import { SAV_REMINDER_LABELS } from '@/services/savRemindersService';
import { savReminderWhen } from '@/utils/savReminderWhen';
import { BellIcon } from '@heroicons/react/24/outline';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const PRIORITY_DOT: Record<number, string> = {
  1: 'bg-red-500',
  2: 'bg-red-500',
  3: 'bg-orange-500',
  4: 'bg-orange-500',
};

/**
 * Cloche « À faire » de l'en-tête (à côté des icônes Messages et SAV) : nombre de dossiers SAV à traiter et fenêtre déroulante qui les
 * liste, du plus urgent au moins urgent. Un clic sur une ligne ouvre le dossier. N'est rendue que pour les comptes ayant le droit
 * « SAV du site ». Un rappel n'envoie jamais rien au client : c'est seulement une aide pour le personnel.
 */
export function SavRemindersButton() {
  const navigate = useNavigate();
  const { allowed, reminders, count, isError, refetch } = useSavReminders();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  if (!allowed) return null;

  const n = count ?? 0;

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => {
          setOpen(o => !o);
          if (!open) void refetch();
        }}
        className="layout-header-btn-hover relative p-2 rounded-token-md focus:outline-none focus:ring-2 focus:ring-token-focus focus:ring-offset-token-focus transition-colors duration-200"
        aria-label={
          n > 0 ? `À faire, ${n} dossier${n > 1 ? 's' : ''} SAV` : 'À faire, rien en attente'
        }
        aria-haspopup="dialog"
        aria-expanded={open}
        title="À faire (dossiers SAV)"
      >
        <BellIcon className="h-6 w-6" />
        {n > 0 && (
          <span
            data-testid="sav-reminders-badge"
            className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold leading-none"
          >
            {n > 99 ? '99+' : n}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="À faire"
          data-testid="sav-reminders-panel"
          className="absolute right-0 top-full mt-2 w-[26rem] max-w-[90vw] rounded-xl border border-default bg-surface text-ink shadow-xl z-50 overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-default bg-blue-50">
            <p className="text-sm font-semibold">À faire ({n})</p>
            <p className="text-xs text-muted">
              Dossiers SAV en attente d'une action. Un rappel disparaît dès que vous agissez.
            </p>
          </div>
          {isError && (
            <p role="alert" className="px-4 py-3 text-sm text-red-700">
              Impossible de charger les rappels.{' '}
              <button
                type="button"
                onClick={() => void refetch()}
                className="underline font-medium"
              >
                Réessayer
              </button>
            </p>
          )}
          {!isError && n === 0 && (
            <p className="px-4 py-6 text-sm text-muted text-center">Rien à faire pour le moment.</p>
          )}
          {n > 0 && (
            <ul className="max-h-[60vh] overflow-y-auto divide-y divide-default">
              {reminders.map(r => (
                <li key={r.request_id}>
                  <button
                    type="button"
                    data-testid="sav-reminder-row"
                    onClick={() => {
                      close();
                      navigate(`${ROUTES.SAV_REQUESTS}?d=${r.request_id}`);
                    }}
                    className="w-full text-left px-4 py-2.5 hover:bg-token-neutral-bg-hover focus:outline-none focus:bg-token-neutral-bg-hover transition-colors"
                  >
                    <span className="flex items-center gap-2">
                      <span
                        aria-hidden="true"
                        className={`h-2 w-2 shrink-0 rounded-full ${PRIORITY_DOT[r.priority] ?? 'bg-gray-400'}`}
                      />
                      <span className="text-sm font-semibold">
                        {SAV_REMINDER_LABELS[r.rule_key]?.short ?? r.rule_key}
                      </span>
                      <span className="ml-auto shrink-0 text-xs text-muted">
                        {savReminderWhen(r)}
                      </span>
                    </span>
                    <span className="mt-0.5 block text-xs text-muted [overflow-wrap:anywhere]">
                      {r.reference} · {r.client_name}
                      {r.store_name ? ` · ${r.store_name}` : ''}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
